<template>
	<div
		data-testid="help-assistant-chat-list"
		class="help-chat-list"
	>
		<v-btn
			ref="newChatButton"
			data-testid="help-assistant-chat-list-new-chat"
			outlined
			small
			:disabled="disabled"
			class="help-chat-list__new-chat"
			@click="$emit('new-chat')"
		>
			New chat
		</v-btn>

		<p
			v-if="loading"
			data-testid="help-assistant-chat-list-loading"
			class="help-chat-list__hint"
		>
			Loading chats…
		</p>
		<p
			v-else-if="chats.length === 0"
			data-testid="help-assistant-chat-list-empty"
			class="help-chat-list__hint"
		>
			No previous chats yet.
		</p>
		<ul
			v-else
			data-testid="help-assistant-chat-list-items"
			class="help-chat-list__items"
		>
			<li
				v-for="chat in chats"
				:key="chat.chatId"
				class="help-chat-list__row"
			>
				<button
					type="button"
					:data-testid="`help-assistant-chat-list-item-${chat.chatId}`"
					:aria-current="chat.chatId === activeChatId ? 'true' : null"
					:disabled="disabled"
					:class="['help-chat-list__select', { 'help-chat-list__select--active': chat.chatId === activeChatId }]"
					@click="$emit('select', { chatId: chat.chatId })"
				>
					<span class="help-chat-list__select-title">
						{{ chat.title }}
					</span>
					<span class="help-chat-list__select-time">
						{{ formatRelativeTime(chat.updatedAt) }}
					</span>
				</button>

				<template v-if="pendingDeleteId === chat.chatId">
					<button
						:ref="`confirm-${chat.chatId}`"
						type="button"
						:data-testid="`help-assistant-chat-list-delete-confirm-${chat.chatId}`"
						aria-label="Confirm delete"
						:disabled="disabled"
						class="help-chat-list__icon-button help-chat-list__icon-button--danger"
						@click="confirmDelete(chat.chatId)"
					>
						<LucideIcon
							name="Check"
							:size="16"
						/>
					</button>
					<button
						type="button"
						:data-testid="`help-assistant-chat-list-delete-cancel-${chat.chatId}`"
						aria-label="Cancel delete"
						:disabled="disabled"
						class="help-chat-list__icon-button"
						@click="cancelDelete(chat.chatId)"
					>
						<LucideIcon
							name="X"
							:size="16"
						/>
					</button>
				</template>
				<button
					v-else
					:ref="`delete-${chat.chatId}`"
					type="button"
					:data-testid="`help-assistant-chat-list-delete-${chat.chatId}`"
					:aria-label="`Delete chat \u0022${chat.title}\u0022`"
					:disabled="disabled"
					class="help-chat-list__icon-button"
					@click="startDelete(chat.chatId)"
				>
					<LucideIcon
						name="Trash2"
						:size="16"
					/>
				</button>
			</li>
		</ul>
	</div>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'
import { formatRelativeTime } from './utils/chat-core.js'

export default {
	name: 'help-chat-list',
	components: {
		LucideIcon
	},
	props: {
		chats: {
			type: Array,
			default: () => []
		},
		activeChatId: {
			type: String,
			default: null
		},
		loading: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			// Chat awaiting a second click to actually delete (two-step, inline confirm - no
			// modal). Reset whenever the chat list itself changes underneath it (e.g. the
			// pending chat was deleted from elsewhere) so a stale confirm row can't linger.
			pendingDeleteId: null,
			// Set the instant a delete is CONFIRMED (as opposed to pendingDeleteId, cleared
			// immediately on confirm). The actual row only unmounts once the parent's delete
			// call succeeds and removes it from `chats` - tracked separately so the watcher
			// below knows when that has happened, to move focus onto New chat rather than
			// letting it drop to <body>.
			confirmedDeleteId: null
		}
	},
	watch: {
		chats () {
			if (this.pendingDeleteId && !this.chats.some(chat => chat.chatId === this.pendingDeleteId)) {
				this.pendingDeleteId = null
			}
			if (this.confirmedDeleteId && !this.chats.some(chat => chat.chatId === this.confirmedDeleteId)) {
				this.confirmedDeleteId = null
				this.$nextTick(() => this.$refs.newChatButton?.$el?.focus())
			}
		}
	},
	methods: {
		formatRelativeTime,
		// Two-step delete is inline (no modal), which means the Delete button that had focus
		// unmounts the instant the row swaps to its confirm/cancel state - left alone, focus
		// drops to <body> and keyboard/screen-reader users lose their place. Move focus onto
		// the row's own Confirm button on entry, and back onto its Delete button on cancel, via
		// $nextTick (the target isn't in the DOM yet on the same tick the v-if flips).
		startDelete (chatId) {
			this.pendingDeleteId = chatId
			this.$nextTick(() => this.focusRef(`confirm-${chatId}`))
		},
		cancelDelete (chatId) {
			this.pendingDeleteId = null
			this.$nextTick(() => this.focusRef(`delete-${chatId}`))
		},
		confirmDelete (chatId) {
			this.pendingDeleteId = null
			this.confirmedDeleteId = chatId
			this.$emit('delete', { chatId })
		},
		// `ref` inside v-for is per-item unique here (`` `confirm-${chatId}` ``), so this is
		// normally the single native <button> element - not the array Vue returns when several
		// v-for iterations share the same static ref name. Handled defensively either way.
		focusRef (name) {
			const target = this.$refs[name]
			const el = Array.isArray(target) ? target[0] : target
			if (el && typeof el.focus === 'function') el.focus()
		}
	}
}
</script>

<style lang="scss" scoped>
.help-chat-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border-bottom: 1px solid var(--border, #E0E2E5);

  &__new-chat {
    align-self: flex-start;
    min-height: 24px;
  }

  &__hint {
    margin: 0;
    padding: 4px 8px;
    font-size: 0.75rem;
    color: var(--muted-foreground, #666E7A);
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 200px;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    list-style: none;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__select {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    padding: 6px 8px;
    text-align: left;
    cursor: pointer;
    background: none;
    border: none;
    border-radius: var(--radius-sm, 4px);
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      background-color: var(--accent, #5C7B9D);

      .help-chat-list__select-title,
      .help-chat-list__select-time {
        color: var(--accent-foreground, #FFFFFF);
      }
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    &--active {
      background-color: var(--accent, #5C7B9D);

      .help-chat-list__select-title,
      .help-chat-list__select-time {
        color: var(--accent-foreground, #FFFFFF);
      }
    }
  }

  &__select-title {
    max-width: 100%;
    overflow: hidden;
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--foreground, #4A5568);
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.15s ease;
  }

  &__select-time {
    font-size: 0.6875rem;
    color: var(--muted-foreground, #666E7A);
    transition: color 0.15s ease;
  }

  &__icon-button {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    min-height: 24px;
    color: var(--muted-foreground, #666E7A);
    cursor: pointer;
    background: none;
    border: none;
    border-radius: var(--radius-sm, 4px);
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      background-color: var(--accent, #5C7B9D);
      color: var(--accent-foreground, #FFFFFF);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    &--danger {
      color: var(--destructive, #E53E3E);
    }
  }
}
</style>
